import React, { useEffect, useMemo, useState } from 'react'
import { Box, CircularProgress, Paper, Typography, makeStyles } from '@material-ui/core'
import { Redirect } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { userProfile } from '@views/profile/state/profileSlice'
import {
  clearMutationMessage,
  createSettlement,
  deleteSettlement,
  loadCommissionData,
  loadExecutives,
  selectExecutiveCommissions
} from './state/executiveCommissionsSlice'
import { CommissionType, SettlementSelection } from './types'
import { conceptKey, currentPeriod, selectableConceptKeys, selectedConceptTotal } from './utils'
import CommissionKpis from './components/CommissionKpis'
import CommissionTable from './components/CommissionTable'
import CommissionGoalProgress, { CommissionGoalStep } from '@views/shared/CommissionGoalProgress'
import PeriodFilters from './components/PeriodFilters'
import SelectionBar from './components/SelectionBar'
import SettlementConfirmDialog from './components/SettlementConfirmDialog'
import SettlementHistory from './components/SettlementHistory'
import {
  canDeleteExecutiveCommissionSettlements,
  canLiquidateExecutiveCommissions,
  canViewExecutiveCommissions
} from './permissions'

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1500,
    margin: '84px auto 0',
    minHeight: 'calc(100vh - 84px)',
    backgroundColor: '#fff'
  },
  title: { fontWeight: 700, color: '#20242a', letterSpacing: 0 },
  sectionTitle: {
    fontWeight: 700,
    color: '#2a2f36',
    marginBottom: theme.spacing(1.5)
  },
  empty: { padding: theme.spacing(6), textAlign: 'center' },
  '@media (max-width: 600px)': {
    root: { padding: theme.spacing(2), marginTop: 72 }
  }
}))

const executiveGoalSteps: CommissionGoalStep[] = [
  { value: 5, label: 'Habilita comisiones' },
  { value: 7, label: 'Bono $500 mil' },
  { value: 10, label: 'Bono $750 mil' },
  { value: 13, label: 'Bono $1 millón' }
]

const ExecutiveCommissionsView: React.FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const { role } = useSelector(userProfile)
  const {
    executives,
    preview,
    settlements,
    loading,
    mutating,
    message,
    mutationMessage,
    lastDeletedSettlementId
  } = useSelector(selectExecutiveCommissions)
  const liquidator = canLiquidateExecutiveCommissions(role)
  const settlementDeleter = canDeleteExecutiveCommissionSettlements(role)
  const allowed = canViewExecutiveCommissions(role)
  const [period, setPeriod] = useState(currentPeriod())
  const [executiveId, setExecutiveId] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [includeGoal, setIncludeGoal] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  useEffect(() => {
    if (liquidator) dispatch(loadExecutives())
  }, [dispatch, liquidator])

  useEffect(() => {
    if (!allowed || (liquidator && !executiveId)) return
    dispatch(
      loadCommissionData({
        period,
        executiveId: liquidator ? executiveId : undefined
      })
    )
  }, [allowed, dispatch, executiveId, liquidator, period])

  useEffect(() => {
    setSelected(selectableConceptKeys(preview?.rows || []))
    setIncludeGoal(preview?.goalBonusState === 'pending')
  }, [executiveId, period, preview])

  useEffect(() => {
    if (message) enqueueSnackbar(message, { variant: 'error' })
  }, [enqueueSnackbar, message])

  useEffect(() => {
    if (!mutationMessage) return
    enqueueSnackbar(mutationMessage, { variant: 'success' })
    dispatch(clearMutationMessage())
    setConfirmOpen(false)
  }, [dispatch, enqueueSnackbar, mutationMessage])

  const selectedItems = useMemo(
    () =>
      Array.from(selected).map(key => {
        const [personId, type] = key.split(':')
        return { personId, type: type as CommissionType }
      }),
    [selected]
  )

  const selectedTotal = useMemo(() => {
    const conceptTotal = selectedConceptTotal(preview?.rows || [], selected)
    return conceptTotal + (includeGoal ? preview?.goalBonus || 0 : 0)
  }, [includeGoal, preview, selected])

  const selectedConcepts = useMemo<SettlementSelection[]>(
    () =>
      (preview?.rows || []).flatMap(row =>
        Object.values(row.concepts)
          .filter(
            concept =>
              concept.state === 'pending' && concept.selectable && selected.has(conceptKey(row.id, concept.type))
          )
          .map(concept => ({
            personId: row.id,
            personName: row.name,
            type: concept.type,
            eventDate: concept.eventDate,
            eventPeriod: concept.eventPeriod,
            officePeriod: concept.officePeriod || row.officePeriod,
            amount: concept.amount
          }))
      ),
    [preview, selected]
  )

  const executiveName = executives.find(item => item._id === executiveId)?.name || preview?.executive.name || ''
  const toggleConcept = (key: string) =>
    setSelected(current => {
      const next = new Set(current)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  const liquidate = () =>
    dispatch(
      createSettlement({
        executiveId: liquidator ? executiveId : undefined,
        period,
        includeGoalBonus: includeGoal,
        items: selectedItems
      })
    )

  if (!allowed) return <Redirect to="/" />

  return (
    <Box className={classes.root}>
      <Typography className={classes.title} variant="h5" gutterBottom>
        Comisiones de ejecutivos
      </Typography>
      <PeriodFilters
        admin={liquidator}
        executives={executives}
        executiveId={executiveId}
        period={period}
        onExecutiveChange={setExecutiveId}
        onPeriodChange={setPeriod}
      />

      {liquidator && !executiveId ? (
        <Paper className={classes.empty}>Selecciona un ejecutivo para consultar sus comisiones.</Paper>
      ) : loading ? (
        <Box className={classes.empty}>
          <CircularProgress />
        </Box>
      ) : preview ? (
        <>
          <CommissionGoalProgress
            currentValue={preview.officeCaseCount}
            countLabel={`${preview.officeCaseCount} casos en oficina`}
            steps={executiveGoalSteps}
          />
          <CommissionKpis
            admin={liquidator}
            includeGoal={includeGoal}
            preview={preview}
            selectedTotal={selectedTotal}
            onIncludeGoalChange={setIncludeGoal}
          />
          <Typography className={classes.sectionTitle} variant="h6">
            Casos y comisiones
          </Typography>
          {liquidator ? (
            <SelectionBar
              conceptCount={selectedItems.length}
              includeGoal={includeGoal}
              mutating={mutating}
              total={selectedTotal}
              onLiquidate={() => setConfirmOpen(true)}
            />
          ) : null}
          <CommissionTable
            key={`${executiveId}:${period}`}
            admin={liquidator}
            rows={preview.rows}
            selected={selected}
            onToggle={toggleConcept}
          />
          <SettlementHistory
            settlements={settlements}
            canDelete={settlementDeleter}
            mutating={mutating}
            lastDeletedSettlementId={lastDeletedSettlementId}
            onDelete={(settlementId, reason) =>
              dispatch(
                deleteSettlement({
                  settlementId,
                  reason,
                  period,
                  executiveId: liquidator ? executiveId : undefined
                })
              )
            }
          />
        </>
      ) : null}

      <SettlementConfirmDialog
        conceptCount={selectedItems.length}
        executiveName={executiveName}
        goalBonus={preview?.goalBonus || 0}
        includeGoal={includeGoal}
        mutating={mutating}
        open={confirmOpen}
        period={period}
        selectedConcepts={selectedConcepts}
        total={selectedTotal}
        onClose={() => setConfirmOpen(false)}
        onConfirm={liquidate}
      />
    </Box>
  )
}

export default ExecutiveCommissionsView
